import {
  BarsArrowDownIcon,
  BarsArrowUpIcon,
  CheckCircleIcon,
  ClockIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { InputType, INVALID_ALL_INPUTS } from 'config'
import { usePermissions } from 'hooks/usePermissions'
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { svgLoading } from 'stories/assets'
import { confirm, formatDate, getPrice3decimal, InputConvert, InputValidate, isHTML, sortByKey } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { Button } from '../Button/Button'
import { Modal } from '../Modal/Modal'
import { OverrideCaclModal } from '../OverrideCalcModal/OverrideCalcModal'

export interface FormTableHeader {
  key: string
  title: string | JSX.Element
  value?: Function
  prefix?: string
  sortable?: boolean
}

interface FormTableProps {
  /**
   * Header of Table
   */
  header: Array<FormTableHeader>
  /**
   * Table data
   */
  data?: Array<Record<string, any>> | null
  /**
   * Table default data
   */
  defaultData?: Array<Record<string, any>> | null
  /**
   * Inputs
   */
  seperateRowKey?: string
  addText?: string
  cancelEditFlag?: number
  disableForValidation?: boolean
  type?: string
  inputs?: Function | Record<string, InputType>
  cols?: number
  // 1: Full Permission
  // 1.5: Can Not Delete, Can Update, Can Submit
  // 2: Can Not Delete, Can Not Update, Can Submit
  // 2.5: Can Not Delete, Can Not Submit, Can Update
  // 3: Can Not Delete, Can Not Submit, Can Not Update
  permission?: number
  showModal?: boolean
  submitText?: string
  noText?: string
  calcKeys?: string[]
  calcVals?: Record<string, Record<string, any>>
  canOverrideCalc?: boolean

  renderAdditionalActions?: ((data: Record<string, any>, index: number) => JSX.Element) | null
  renderRawDescription?: ((data: Record<string, any>, index: number) => JSX.Element) | null
  initRow?: ((data: Record<string, any>, index: number) => Record<string, any>) | null
  onSubmit?: ((id: any, data: Record<string, any>, finishEditing?: boolean) => Record<string, any>) | null
  onRemove?: ((id: any, index: number) => void) | null
  onChange?: ((data: Record<string, any>[]) => void) | null
  onChangeInputs?: ((data: Record<string, InputType>, key: string) => void) | null
  onCancel?: Function | null
  onAddPress?: Function | null
  inputLogic?:
    | ((
        data: Record<string, InputType>,
        currentId?: number,
        currentKey?: string,
        values?: Record<string, any>,
      ) => Record<string, any>)
    | null
  showHistory?: ((headers: Array<FormTableHeader>) => void) | null
  showFieldHistory?: ((id: number, key: string) => void) | null
  canOverride?: boolean
}

const _FormTable = (
  {
    header = [],
    data: _data = null,
    defaultData = null,
    inputs: _inputs = {},
    cols = 2,
    permission = 1,
    initRow = null,
    inputLogic = null,
    seperateRowKey = '',
    addText = '',
    cancelEditFlag = 0,
    disableForValidation = false,
    showModal = false,
    submitText = 'Submit',
    renderAdditionalActions = null,
    renderRawDescription = null,
    onSubmit: _onSubmit = null,
    onRemove: _onRemove = null,
    onChange: _onChange = null,
    onChangeInputs: _onChangeInputs = null,
    onCancel: _onCancel = null,
    onAddPress: _onAddPress = null,
    showHistory: _showHistory = null,
    showFieldHistory = null,
    noText = '',
    canOverride = false,
    calcKeys = [],
    calcVals,
    canOverrideCalc,
  }: FormTableProps,
  ref: React.Ref<any>,
) => {
  const [loading, setLoading] = useState(false)
  const [currentId, setCurrentId] = useState(0)
  const [data, setData] = useState<Array<Record<string, any>>>([])
  const [inputs, setInputs] = useState<Record<string, InputType>>({})
  const [status, setStatus] = useState(0)
  const [ovData, setOVData] = useState<any>({})
  const [sortKey, setSortKey] = useState<string | null>(null)
  const [sortOrder, setSortOrder] = useState<1 | -1>(1)

  const currentIndex = useMemo(() => {
    if (!currentId) return null
    const index = data.findIndex((item) => item.id == currentId)
    if (index == -1) return null
    return index
  }, [_data, currentId])

  const { hasPermission } = usePermissions()

  const initInputs = () => {
    if (typeof _inputs === 'function') setInputs(_inputs())
    else setInputs(_inputs)
  }

  const onSort = (key: string) => {
    if (!key) return
    if (sortKey === key) {
      setSortOrder(-sortOrder as 1 | -1)
    } else {
      setSortKey(key)
      setSortOrder(1)
    }
  }

  const sortedData = useMemo(() => {
    if (!sortKey) return data
    return sortByKey(data, sortKey, sortOrder)
  }, [data, sortKey, sortOrder])

  useEffect(() => {
    if (addText.length > 0) setStatus(-1)
  }, [addText])

  useEffect(() => {
    if (cancelEditFlag > 0) setStatus(-1)
  }, [cancelEditFlag])

  useEffect(() => {
    initInputs()
  }, [_inputs])

  useEffect(() => {
    if (!defaultData || data.length) return
    if (initRow) {
      defaultData = defaultData.map((item, index) => {
        return initRow(item, index)
      })
    }
    setData(defaultData)
  }, [defaultData])

  useEffect(() => {
    if (!_data) return
    if (initRow) {
      _data = _data.map((item, index) => {
        return initRow(item, index)
      })
    }
    setData(_data)
  }, [_data])

  useImperativeHandle(ref, () => ({
    submit() {
      return onSubmit(false)
    },
    updateInputs(inputUpdates: Record<string, Record<string, any>>) {
      let newInputs = cloneDeep(inputs)
      Object.keys(inputUpdates).forEach((key) => {
        newInputs[key] = { ...newInputs[key], ...inputUpdates[key] }
      })
      setInputs(newInputs)
    },
  }))

  const resetInputs = async (values: Record<string, any> = {}, id: number) => {
    let newInputs = cloneDeep(inputs)
    Object.keys(newInputs).forEach((key) => {
      newInputs[key].value = InputConvert(newInputs[key], values[key])
      newInputs[key].error = ''
    })
    if (inputLogic !== null) {
      const newLogic = inputLogic(newInputs, id, '', values)
      if (newLogic instanceof Promise) {
        setLoading(true)
        newInputs = await newLogic
        setLoading(false)
      } else newInputs = newLogic
    }
    setInputs(newInputs)
  }

  const onChange = async (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)

    newInputs[key].value = value
    newInputs[key].error = InputValidate(newInputs[key])
    if (inputLogic !== null) {
      const newLogic = inputLogic(newInputs, currentId, key)
      if (newLogic instanceof Promise) {
        setLoading(true)
        newInputs = await newLogic
        setLoading(false)
      } else newInputs = newLogic
    }
    setInputs(newInputs)
    _onChangeInputs && _onChangeInputs(newInputs, key)
  }

  const onRemove = async (id: any, index: number) => {
    const content = (
      <div className="mb-4 text-[18px]">
        Do you want to remove <span className="font-bold">No.{index + 1}</span> item?
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    _onRemove && (await _onRemove(id, index))
    if (!_data) {
      const newData = cloneDeep(data)
      newData.splice(index, 1)
      setData(newData)
      _onChange && _onChange(newData)
    }
    onCancelEdit()
  }

  const onEdit = (id: number, index: number) => {
    if (currentId && id === currentId) {
      onCancelEdit()
      return
    }
    setCurrentId(id)
    setStatus(0)
    resetInputs(data[index], id)
  }

  const isFormValidated = useMemo(() => {
    if (!disableForValidation) return true

    let hasError = false
    Object.keys(inputs).forEach((key) => {
      const { visible } = inputs[key]
      if (visible && typeof visible === 'function' && !visible(inputs)) return null
      if (inputs[key].visible === false) return null
      if (InputValidate(inputs[key])) hasError = true
    })
    return !hasError
  }, [inputs])

  const onSubmit = async (finishEditing = true) => {
    const newInputs = cloneDeep(inputs)
    let values: Record<string, any> = {}
    let hasError = false
    Object.keys(inputs).forEach((key) => {
      const { visible } = inputs[key]
      if (visible && typeof visible === 'function' && !visible(inputs)) return null
      if (inputs[key].visible === false) return null
      newInputs[key].error = InputValidate(newInputs[key])
      const { value, error } = newInputs[key]
      if (error) hasError = true
      values[key] = value
    })
    setInputs(newInputs)

    if (hasError) {
      let result = false
      if (canOverride)
        result = (await confirm('Are you sure you want to override saving required fields?', {
          titleNo: `No, I don't`,
        })) as any
      if (!result) {
        toast(INVALID_ALL_INPUTS, { type: 'error' })
        return false
      }
    }

    setLoading(true)
    if (_onSubmit) {
      try {
        values = await _onSubmit(currentId, values, finishEditing)
      } catch (e) {
        setLoading(false)
        return
      }
    } else
      values = {
        id: Date.now(),
        ...values,
      }
    if (finishEditing) {
      setCurrentId(0)
      initInputs()
    } else setCurrentId(values.id)

    setLoading(false)
    if (currentId) {
      if (!_data) {
        const newData = cloneDeep(data)
        const index = newData.findIndex((item) => item.id == currentId)
        newData[index] = {
          id: currentId,
          ...values,
        }
        setData(newData)
        _onChange && _onChange(newData)
      }
      finishEditing && setStatus(2)
    } else {
      if (!_data) {
        const newData = cloneDeep(data)
        newData.push(values)
        setData(newData)
        _onChange && _onChange(newData)
      }
      finishEditing && setStatus(1)
    }
    return values.id
  }

  const onCancelEdit = () => {
    setCurrentId(0)
    initInputs()
    if (addText.length > 0) {
      setStatus(-1)
    }
    if (_onCancel) _onCancel()
  }

  const closeOVModal = async (save: false, data: any) => {
    if (save) {
      onChange(ovData.overrideKey, data.ovValue)
      await onChange(ovData.overrideKey, data.ovValue)
    }
    setOVData({})
  }

  const getCanUpdateRow = () => {
    return data.find((item) => item.id == currentId)?.canUpdateRow || false
  }

  const renderAction = () => {
    if (permission === 3) return null
    if (currentId == 0) {
      if (permission == 2.5) return null
      return (
        <>
          <Button disabled={!isFormValidated} onClick={onSubmit} className="px-10" loading={loading}>
            {submitText}
          </Button>
          {addText.length > 0 && (
            <Button onClick={onCancelEdit} color="white" className="px-10 py-1">
              Cancel
            </Button>
          )}
        </>
      )
    }
    const canUpdateRow = getCanUpdateRow()
    return (
      <>
        <Button
          onClick={onSubmit}
          className="px-10 py-1"
          loading={loading}
          disabled={(![1, 1.5, 2.5].includes(permission) && !canUpdateRow) || !isFormValidated}
        >
          Update
        </Button>
        <Button onClick={onCancelEdit} color="white" className="px-10 py-1">
          Cancel
        </Button>
      </>
    )
  }

  const headerActionFragment = useMemo(() => {
    if (permission === 3) return null
    return (
      <th scope="col" className="px-2 py-3 ">
        <span>
          <div className="group flex items-center gap-2">
            Action
            {hasPermission('CAN_VIEW_AUDIT_LOG') && _showHistory && (
              <span
                className="hidden group-hover:inline text-shade-blue dark:text-gray-400 cursor-pointer hover:underline text-[13px]"
                onClick={() => _showHistory(header)}
              >
                <ClockIcon className="w-4 h-4" aria-hidden="true" />
              </span>
            )}
          </div>
        </span>
      </th>
    )
  }, [permission])

  const renderActionTD = (noRowSpans: number, row: any, index: number) => {
    if (permission === 3) return null
    return (
      <td className="px-2 py-2" rowSpan={noRowSpans}>
        <div className="flex gap-1">
          <span
            className="text-shade-blue cursor-pointer hover-shadow1 p-1 rounded"
            onClick={() => onEdit(row.id, index)}
          >
            <PencilSquareIcon className="w-4 h-4"></PencilSquareIcon>
          </span>
          {permission === 1 && (
            <span
              className="text-red-800 cursor-pointer hover-shadow1 p-1 rounded"
              onClick={() => onRemove(row.id, index)}
            >
              <TrashIcon className="w-4 h-4"></TrashIcon>
            </span>
          )}
        </div>
        <div className="flex">{renderAdditionalActions && renderAdditionalActions(row, index)}</div>
      </td>
    )
  }

  const renderEditSection = () => {
    if (permission === 3) return null
    if (status === -1) {
      if (addText.length > 0) {
        return (
          <div
            className="flex flex-wrap items-center px-2 py-1 rounded shadow w-fit text-shade-blue hover:underline cursor-pointer mx-4 gap-2"
            onClick={async () => {
              if (!_onAddPress || (await _onAddPress())) setStatus(0)
            }}
          >
            <PlusIcon className="w-4 h-4"></PlusIcon>
            <span className="text-[14px]">{addText}</span>
          </div>
        )
      }
    }

    if (status === 1) {
      //After Adding Success
      return (
        <div
          className="mx-2 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4 text-[15px] flex flex-wrap items-center"
          role="alert"
        >
          <CheckCircleIcon className="w-6 h-6"></CheckCircleIcon>
          <span className="ml-1">Successfully Added!</span>
          <span className="font-variation-settings-600 ml-3 mr-1 cursor-pointer underline" onClick={() => setStatus(0)}>
            Click Here
          </span>
          to create new item.
        </div>
      )
    }

    if (status === 2) {
      //After Update Success
      return (
        <div
          className="mx-2 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-4 text-[15px] flex flex-wrap items-center"
          role="alert"
        >
          <CheckCircleIcon className="w-6 h-6"></CheckCircleIcon>
          <span className="ml-1">Successfully Updated!</span>
          {permission != 2.5 && (
            <>
              <span
                className="font-variation-settings-600 ml-3 mr-1 cursor-pointer underline"
                onClick={() => setStatus(0)}
              >
                Click Here
              </span>
              to create new item.
            </>
          )}
        </div>
      )
    }

    if (status === 0) {
      if (permission == 2.5 && currentIndex === null) return null

      const content = (
        <div className="px-4">
          {permission !== 3 && (
            <div className={`grid gap-4 md:grid-cols-${cols} grid-cols-1 mb-3`}>
              {Object.keys(inputs).map((key, index) => {
                const input: any = cloneDeep(inputs[key])
                if (typeof input.visible === 'function' && !input.visible(inputs)) return null
                if (input.visible === false) return null
                if (calcKeys.includes(key) && calcVals?.[currentId]?.[key]) {
                  input.disabled = true
                  if (canOverrideCalc) {
                    input.additionalElements = (
                      <span
                        className="text-shade-blue cursor-pointer"
                        onClick={() => {
                          setOVData({
                            title: input.title,
                            overrideKey: key,
                            calcValue: calcVals?.[currentId]?.[key] || '',
                            ovValue: input.value || '',
                          })
                        }}
                      >
                        <PencilSquareIcon className="w-4 h-4" />
                      </span>
                    )
                  }
                  input.value = input.value || calcVals?.[currentId]?.[key]
                  if (input.value != calcVals?.[currentId]?.[key]) input.bgColor = 'red-200'
                }

                if (input.inputType === 'divider') {
                  return (
                    <div className={`input md:col-span-${cols} -mx-2`} key={index}>
                      <div className="border p-2 rounded bg-gray-100 font-variation-settings-600 text-[14px]">
                        {input.title}
                      </div>
                    </div>
                  )
                }
                if (input.inputType === 'alert') {
                  return (
                    <div className={`input md:col-span-${cols}`} key={index}>
                      <div
                        className="bg-blue-100 border border-blue-400 text-blue-700 px-4 py-3 rounded relative text-[15px]"
                        role="alert"
                      >
                        <span dangerouslySetInnerHTML={{ __html: input.value }} />
                      </div>
                    </div>
                  )
                }
                if (permission === 2 && currentIndex !== null) {
                  if (!getCanUpdateRow()) input.disabled = true
                }

                if (typeof input.value === 'function') input.value = input.value(inputs)

                if (input.isLoading)
                  input.additionalElements = (
                    <img src={svgLoading} className="w-[14px] h-[14px] text-white animate-spin" />
                  )

                return (
                  <div className={`input md:col-span-${input.span || 1}`} key={index}>
                    <RenderInput
                      input={input}
                      Key={key}
                      onChange={onChange}
                      showHistory={(key: string) => showFieldHistory && showFieldHistory(currentId, key)}
                    />
                  </div>
                )
              })}
            </div>
          )}
          <div className="text-center flex justify-center mt-4">{renderAction()}</div>
        </div>
      )
      if (!showModal)
        return (
          <div>
            {currentIndex !== null ? (
              <p className="text-md mb-3 border-b text-shade-blue ml-2">Updating No. {currentIndex + 1}</p>
            ) : null}
            {content}
          </div>
        )

      return (
        <Modal
          title={currentIndex !== null ? `Updating No. ${currentIndex + 1}` : 'Add New'}
          titleOkay=""
          titleCancel=""
          hiddenX
          isOpen
          loading={loading}
        >
          <div className="w-196">{content}</div>
        </Modal>
      )
    }
  }

  return (
    <div className="Formtable-container">
      <div className="overflow-auto mb-6 shadow-md sm:rounded-lg">
        <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="px-2 py-3">
                {noText || 'No'}
              </th>
              {header.map((item) => {
                const active = sortKey === item.key
                return (
                  <th
                    scope="col"
                    className={`px-2 py-3 ${item?.sortable && 'cursor-pointer'}`}
                    key={item.key}
                    onClick={() => {
                      if (item?.sortable) onSort(item.key)
                    }}
                  >
                    <div className={item?.sortable ? 'flex item-center gap-1' : ''}>
                      {typeof item.title === 'string' ? (
                        <span dangerouslySetInnerHTML={{ __html: item.title }} />
                      ) : (
                        item.title
                      )}
                      {item?.sortable &&
                        (active && sortOrder === 1 ? (
                          <BarsArrowUpIcon className="w-3 h-3 ml-2" />
                        ) : active && sortOrder === -1 ? (
                          <BarsArrowDownIcon className="w-3 h-3 ml-2" />
                        ) : (
                          <div className="w-3 h-3 ml-2" />
                        ))}
                    </div>
                  </th>
                )
              })}
              {headerActionFragment}
            </tr>
          </thead>
          <tbody className="text-[14px] text-gray-900">
            {sortedData.map((row, index: number) => {
              let noRowSpans = 1
              let showSecondRow = false
              if (row[seperateRowKey]?.length > 0) {
                showSecondRow = true
              }
              if (showSecondRow) noRowSpans = 2
              let trBorderColor = currentIndex === index ? 'bg-gray-100' : ''
              return [
                renderRawDescription && renderRawDescription(row, index),
                <tr key={index} className={`border-b ${trBorderColor} ${row.tdClass}`}>
                  <td rowSpan={noRowSpans} className="px-2 py-2 pl-3 font-medium text-gray-900 whitespace-nowrap">
                    {index + 1}
                  </td>
                  {header.map((header, rIndex) => {
                    let rlt = header.value ? header.value(row) : row[header.key]
                    let options = (inputs[header.key] as any)?.options
                    if (rlt === undefined) rlt = ''
                    if (inputs[header.key]?.type === 'thousandSep' && rlt !== undefined) rlt = getPrice3decimal(rlt)
                    if (inputs[header.key]?.type === 'date' && rlt !== undefined) rlt = formatDate(rlt)
                    if (
                      inputs[header.key]?.inputType === 'select' &&
                      rlt !== undefined &&
                      typeof options === 'object' &&
                      !Array.isArray(options)
                    )
                      rlt = options[rlt]
                    if (typeof rlt == 'boolean') rlt = rlt ? 'Yes' : 'No'
                    if (inputs[header.key]?.prefix === '$') rlt = `$${rlt}`
                    if (header.prefix) rlt = `${header.prefix}${rlt}`
                    return (
                      <td className="px-2 py-2" key={rIndex}>
                        {isHTML(rlt) ? (
                          <span className="dangerouslySetInnerHTML">
                            <div dangerouslySetInnerHTML={{ __html: rlt }}></div>
                          </span>
                        ) : (
                          <span className="">{rlt}</span>
                        )}
                      </td>
                    )
                  })}
                  {renderActionTD(noRowSpans, row, index)}
                </tr>,
                showSecondRow && (
                  <tr className={`border-b ${trBorderColor}`} key={`1-${index}`}>
                    <td className="px-2 py-2" colSpan={header.length}>
                      <span className="italic">{row[seperateRowKey]}</span>
                    </td>
                  </tr>
                ),
              ]
            })}
          </tbody>
        </table>
      </div>
      {Object.keys(ovData).length > 0 && (
        <OverrideCaclModal
          title={ovData.title}
          calcValue={ovData.calcValue}
          ovValue={ovData.ovValue}
          onClose={closeOVModal}
        />
      )}

      {renderEditSection()}
    </div>
  )
}

export const FormTable = forwardRef(_FormTable)
